import { Container } from "pixi.js";
import { fitWithin } from "@slot/presentation";
import type { ContentAreaStore, DesignRect, Log, PresentationTaskRunner, ViewportStore } from "@slot/presentation";
import type { GameLogoSpec, LocalizationService } from "@slot/game-shell";
import type { GameVisualAssets } from "../assets/GameVisualAssets";
import { BASE_ROWS, GRID_SIZE } from "../gridSize";
import type { PotLevels } from "../rules/symbols";
import { ART_LOAD_TUNING } from "../tuning/featureTuning";
import { ArtLoads } from "./ArtLoads";
import { artResolution } from "./artResolution";
import { BonusTotalView } from "./BonusTotalView";
import { CellFires } from "./CellFires";
import { CoinShines } from "./CoinShines";
import { Collectors } from "./Collectors";
import { EffectsLayer } from "./EffectsLayer";
import { multiplierLabel } from "./MultiplierBadgesView";
import { PotCoinBursts } from "./PotCoinBursts";
import { GridContainer } from "./GridContainer";
import { GridEdgeFire } from "./GridEdgeFire";
import { JackpotPlaquesView } from "./JackpotPlaquesView";
import { LineSelectorView } from "./LineSelectorView";
import { COMPOSITION, COMPOSITION_FIT, LOGO_LAYOUT } from "./layout";
import { PotsView } from "./PotsView";
import { RespinMeterView } from "./RespinMeterView";
import { FX_SCOPE } from "./fxScope";

/** What the dev hook reads off the round presentation. */
export interface SceneReadout {
  /** The board as the cells show it, rows top to bottom ("" for an empty cell). */
  board(): string[][];
  /** The pots' gems as they show. */
  pots(): PotLevels;
  /** The cell multipliers on the board, by cell. */
  multipliers(): ReadonlyMap<number, number>;
  /** The rows lit as the active lines' (decision 56). */
  litRows(): readonly number[];
  /** The cells whose empty-cell badge is the lit one (decision 56). */
  litBlanks(): readonly number[];
  /** The gold's shine now: what glints (by name), the round's moments among it, and how many sparkles twinkle. */
  shine(): { glinting: readonly string[]; moments: readonly string[]; sparkles: number };
  /** The cells the bonus's total has taken as its board pays (darkened as paid), in index order. */
  paid(): readonly number[];
}

/** The composition's authored size: the plaques' top to the meter's bottom with the base rows, the frame's width. */
export const COMPOSITION_SIZE = { width: COMPOSITION.width, height: COMPOSITION.bottom - COMPOSITION.top } as const;

/** The logo between the plaques' columns, on every shape (its art is 1100 × 760). */
export const GAME_LOGO: GameLogoSpec = {
  box: { width: LOGO_LAYOUT.width, height: LOGO_LAYOUT.height },
  sprite: { width: LOGO_LAYOUT.width, height: LOGO_LAYOUT.height },
  placement: {
    desktop: { x: LOGO_LAYOUT.x, y: LOGO_LAYOUT.y },
    mobile: { x: LOGO_LAYOUT.x, y: LOGO_LAYOUT.y },
    mobile_landscape: { x: LOGO_LAYOUT.x, y: LOGO_LAYOUT.y },
  },
};

/**
 * The scene (the shell draws its background under it: decision 69): the composition fitted per shape — the pots and the boosters' plumes out of them, then
 * the composition root over them: the jackpot plaques, the grid, the respin meter, the bonus's total and the flights
 * over everything. The shell adds the logo as the root's first child and its loading overlay as its last, so the plumes
 * rise behind the logo and the plaques, as the artist layers them. GROW grows the grid by its two rows downwards; the
 * composition zooms out with it so it keeps clear of the bar.
 */
export class GameScene extends Container {
  /** The shell's root (the logo, the loading overlay) and everything over the pots, at the composition's origin. */
  public readonly compositionRoot: Container;
  public readonly plaques: JackpotPlaquesView;
  public readonly pots: PotsView;
  public readonly grid: GridContainer;
  public readonly meter: RespinMeterView;
  /** The LINES selector under the grid in the base game (decision 56). */
  public readonly lineSelector: LineSelectorView;
  public readonly bonusTotal: BonusTotalView;
  public readonly fx: EffectsLayer;
  /** GROW's fire on the grid's frame. */
  public readonly edgeFire: GridEdgeFire;
  /** The cells that hold a multiplier burning (MULTI's). */
  public readonly cellFires: CellFires;
  /** The coins a pot spills as a gem's light reaches it. */
  public readonly coinBursts: PotCoinBursts;
  /** The artist's collectors, lent to the collect coins on the board (`CoinSymbolView`). */
  public readonly collectors: Collectors;
  /** The artist's idle coin, its variants shown by the plain coins on the board (`CoinSymbolView`). */
  public readonly coinShines: CoinShines;
  /** What the round presentation shows, for the dev hook (the presentation sets it); null before it exists. */
  public readout: SceneReadout | null = null;
  /** What is fitted per shape: the pots' layer under the composition root. */
  private readonly composition: Container;
  private readonly viewport: ViewportStore;
  private readonly runner: PresentationTaskRunner;
  /** The art loading after the start (`trackArt`), which a round holds for. */
  private readonly artLoads: ArtLoads;
  private readonly disposers: Array<() => void> = [];
  private area: DesignRect | null = null;
  /** 0 with the base rows, 1 with all five (fractional while the grid grows). */
  private growth = 0;

  public constructor(input: {
    assets: GameVisualAssets;
    viewport: ViewportStore;
    contentArea: ContentAreaStore;
    localization: LocalizationService;
    runner: PresentationTaskRunner;
    /** The UI's runner: what plays while the game waits, never sped up with the round (the meter's flames, the artist's animations). */
    ambientRunner: PresentationTaskRunner;
    format: (minor: string) => string;
    log: Log;
  }) {
    super({ label: "GameScene" });
    const { assets } = input;
    const t = assets.textures;
    this.viewport = input.viewport;
    this.runner = input.runner;
    this.artLoads = new ArtLoads({ ambientRunner: input.ambientRunner, maxWaitSeconds: ART_LOAD_TUNING.maxWaitSeconds });

    this.composition = new Container({ label: "GameComposition" });
    const potsLayer = new Container({ label: "PotsLayer" });
    this.compositionRoot = new Container({ label: "GameCompositionRoot" });
    this.composition.addChild(potsLayer, this.compositionRoot);
    this.addChild(this.composition);

    this.plaques = new JackpotPlaquesView({ textures: t.plaques, format: input.format, runner: input.runner });
    this.pots = new PotsView({ pots: t.pots, labels: t.potLabels, heap: t.coinsHeap, piles: t.piles, glow: t.glow, runner: input.runner, ambientRunner: input.ambientRunner, log: input.log });
    this.grid = new GridContainer({ frame: t.frame, rowFrame: t.rowFrame, cellBlue: t.cellBlue, cellRed: t.cellRed, runner: input.runner });
    this.meter = new RespinMeterView({ frame: t.meterFrame, slotOn: t.meterSlotOn, slotOff: t.meterSlotOff, runner: input.runner, ambientRunner: input.ambientRunner });
    this.meter.hangFrom(this.grid.frameBottom());
    this.lineSelector = new LineSelectorView({ panel: t.linesPanel, arrow: t.linesArrow, title: input.localization.t("lines.title", "LINES") });
    this.lineSelector.hangFrom(this.grid.frameBottom());
    this.bonusTotal = new BonusTotalView({ format: input.format, runner: input.runner });
    this.bonusTotal.position.copyFrom(this.meter.position);
    this.edgeFire = new GridEdgeFire({ ambientRunner: input.ambientRunner, log: input.log });
    this.grid.frameOverlay.addChild(this.edgeFire);
    this.cellFires = new CellFires({ ambientRunner: input.ambientRunner, log: input.log, cellCentre: (index) => this.grid.cellCentre(index) });
    this.grid.cellFireLayer.addChild(this.cellFires);
    this.fx = new EffectsLayer({
      glow: t.glow,
      sparkle: t.sparkle,
      runner: input.runner,
      ambientRunner: input.ambientRunner,
      log: input.log,
      // A MULTI streak's number is the cell's own, carried; MULTI's passers fall past the screen's bottom (its CSS size).
      streakNumber: multiplierLabel,
      screen: () => input.viewport.getSnapshot().css,
    });
    this.collectors = new Collectors({ ambientRunner: input.ambientRunner, log: input.log });
    this.disposers.push(() => this.collectors.dispose());
    this.coinShines = new CoinShines({ ambientRunner: input.ambientRunner, log: input.log });
    this.disposers.push(() => this.coinShines.dispose());
    this.coinBursts = new PotCoinBursts({
      coin: assets.symbols.coin,
      openings: { life: this.pots.mouth("life"), multi: this.pots.mouth("multi"), grow: this.pots.mouth("grow") },
      viewport: input.viewport,
      ambientRunner: input.ambientRunner,
      screen: () => input.viewport.getSnapshot().css,
    });
    // As the mock-up stacks them: the artist's shine behind the pots, the pots and their heap of coins behind the grid (it
    // hides the heap's lower half), the pots' names in front of it; the boosters' plumes over the pots, behind the
    // plaques and the logo, and the coins a pot spills over them, falling back behind the frame.
    potsLayer.addChild(this.pots.shine, this.edgeFire.behindPots, this.pots, this.pots.smoke, this.coinBursts);
    this.compositionRoot.addChild(this.plaques, this.grid, this.pots.names, this.meter, this.lineSelector, this.bonusTotal, this.fx);

    this.disposers.push(
      input.contentArea.subscribe((area) => {
        this.area = area;
        this.applyFit();
      }),
    );
  }

  /** A load of the art the scene shows after the start (the artist's animations, the deferred bundle): a round waits for it. */
  public trackArt(load: Promise<unknown>): void {
    this.artLoads.track(load);
  }

  /**
   * Settles once every piece of art loading after the start is in or has failed (its stand-in then stays), or after the
   * tuning's longest wait: a round holds for it before it starts (decision 71), so none plays its moments with the
   * stand-ins of art still on its way.
   */
  public whenArtReady(): Promise<void> {
    return this.artLoads.whenReady();
  }

  /** Grows the grid to all five rows (GROW in play) or back to the base rows, over `seconds` of the round's runner. */
  public async setGrown(grown: boolean, seconds: number): Promise<void> {
    const from = this.growth;
    const to = grown ? 1 : 0;
    if (from === to) return;
    await this.runner.tweenProgress(seconds, (k) => this.applyGrowth(from + (to - from) * k), FX_SCOPE, { easing: "cubicInOut" });
    this.applyGrowth(to);
  }

  /** The grid's state at once (a recovery, a replay's start). */
  public snapGrown(grown: boolean): void {
    this.applyGrowth(grown ? 1 : 0);
  }

  public get isGrown(): boolean {
    return this.growth >= 1;
  }

  public dispose(): void {
    for (const dispose of this.disposers) dispose();
    this.destroy({ children: true });
  }

  private applyGrowth(growth: number): void {
    this.growth = growth;
    const rows = BASE_ROWS + (GRID_SIZE.rows - BASE_ROWS) * growth;
    this.grid.layoutRows(rows);
    this.edgeFire.follow(rows);
    this.meter.hangFrom(this.grid.frameBottom(rows));
    // The bonus's total plaque takes the meter's place as the board pays.
    this.bonusTotal.position.copyFrom(this.meter.position);
    this.applyFit();
  }

  /** The composition's fit for the shape: the base rows' and all five's, blended by how far the grid has grown. */
  private applyFit(): void {
    if (!this.area) return;
    const { shape, scale: cssPerUnit } = this.viewport.getSnapshot();
    const base = fitWithin(COMPOSITION_FIT.base, shape, this.area);
    // The artist's animations' size on the screen with the base rows (the larger of the two fits), in device pixels per unit.
    const pixelsPerUnit = base.scale * cssPerUnit * artResolution(shape);
    this.pots.setPixelsPerUnit(pixelsPerUnit);
    this.fx.lights.setPixelsPerUnit(pixelsPerUnit);
    this.fx.impacts.setPixelsPerUnit(pixelsPerUnit);
    this.fx.eruptions.setPixelsPerUnit(pixelsPerUnit);
    this.fx.streaks.setPixelsPerUnit(pixelsPerUnit);
    this.fx.boardImpact.setPixelsPerUnit(pixelsPerUnit);
    this.fx.cellHits.setPixelsPerUnit(pixelsPerUnit);
    this.cellFires.setPixelsPerUnit(pixelsPerUnit);
    this.collectors.setPixelsPerUnit(pixelsPerUnit);
    this.coinShines.setPixelsPerUnit(pixelsPerUnit);
    this.edgeFire.setPixelsPerUnit(pixelsPerUnit);
    const grown = fitWithin(COMPOSITION_FIT.grown, shape, this.area);
    const k = this.growth;
    const scale = base.scale + (grown.scale - base.scale) * k;
    this.composition.scale.set(scale, scale);
    this.composition.position.set(base.x + (grown.x - base.x) * k, base.y + (grown.y - base.y) * k);
  }
}
